/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  FormLabel,
  FormErrorMessage,
  Modal,
  Textarea,
  Input,
  Divider,
  Grid,
  Badge,
  Icon,
  InputGroup,
  HStack,
  Avatar,
  Center,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from '@chakra-ui/react';
import { useProtocolPage } from './hooks/useProtocolPage';
import BottleItem from '../../../components/BottleEditor';
import MultiSelectDropdown from 'components/MultiSelectDropdown';
import SelectedChips from 'components/SelectedChips';
import './protocol.css';
import { usePatientList } from '../patientManagement/hooks/usePatientList';
import useDebounce from 'constants/helper/useDebounce';
import PatientListModal from './hooks/PatientListModel';
import { useSavePatientTreatmentProtocol } from './hooks/useSavePatientTreatmentProtocol';
import {
  CheckCircleIcon,
  EditIcon,
  InfoIcon,
  RepeatIcon,
  StarIcon,
  ViewIcon,
} from '@chakra-ui/icons';
import { useAddRemark } from '../patientHistoryManagement/hooks/useAddRemark';
import { useProtocolDetailsByObjId } from './hooks/useProtocolDetailsByObjId';

export default function ProtocolPage() {
  /* ===================== STATES ===================== */
  const [selectedSystems, setSelectedSystems] = useState([]);
  const [activeDisease, setActiveDisease] = useState('');
  const [fieldErrorsForPatientRemedy, setFieldErrorsForPatientRemedy] =
    useState({});
  const [diseases, setDiseases] = useState([]);
  const [selectedDiseases, setSelectedDiseases] = useState([]);
  const [selectedDiseaseObjects, setSelectedDiseaseObjects] = useState([]);
  const [finalDiseases, setFinalDiseases] = useState([]);
  const [finalSystems, setFinalSystems] = useState([]);
  const [protocolData, setProtocolData] = useState({});
  const [isEditAll, setIsEditAll] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isEditAllForPatient, setIsEditAllForPatient] = useState(true);
  const [editData, setEditData] = useState({});
  const [editDataForPatient, setEditDataForPatient] = useState({});
  const [fullData, setFullData] = useState({});
  const [protocolObjectId, setProtocolObjectId] = useState(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);

  const [bp, setBp] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [systolic, setSystolic] = useState('');
  const [sugar, setSugar] = useState('');
  const [notes, setNotes] = useState('');

  const { patientHistory, totalPages, isLoading } = usePatientList(
    page,
    debouncedSearch,
  );
  const toast = useToast();
  const [errors, setErrors] = useState({
    systems: '',
    diseases: '',
  });
  const [fieldErrors, setFieldErrors] = useState({
    bottle1: { R1: '' },
    bottle2: { R1: '' },
    bottle3: { R1: '' },
  });

  const isProtocolReady =
    !!protocolData && Object.keys(protocolData).length > 0;

  const [isRemarkOpen, setIsRemarkOpen] = useState(false);

  const [remarkData, setRemarkData] = useState({
    score: '',
    reliefPercent: {},
    remark: '',
  });

  const handleRemarkChange = (fieldOrId, value) => {
    // Check if the key corresponds to a disease ID by looking it up in the data
    const isDiseaseId = data?.diseases?.some((d) => d._id === fieldOrId);

    if (isDiseaseId) {
      setRemarkData((prev) => ({
        ...prev,
        reliefPercent: {
          ...prev.reliefPercent,
          [fieldOrId]: value,
        },
      }));
    } else {
      setRemarkData((prev) => ({ ...prev, [fieldOrId]: value }));
    }
  };

  const isScoreValid = Number(remarkData.score) > 0;

  const isRemarkValid = remarkData.remark.trim().length > 0;

  const { mutate: addRemarkMutation, isLoading: isAddingRemark } = useAddRemark(
    {
      onSuccess: () => {
        setIsRemarkOpen(false);
        setRemarkData({
          score: '',
          reliefPercent: '',
          remark: '',
        });
      },
    },
  );

  /* ===================== DARK MODE ===================== */
  /* ===================== COLOR SYSTEM ===================== */
  const pageBg = useColorModeValue('blue.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const rightBg = useColorModeValue('gray.50', 'gray.700');
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  const chipBg = useColorModeValue('blue.100', 'blue.700');
  const chipText = useColorModeValue('gray.800', 'white');
  const borderClr = useColorModeValue('gray.300', 'gray.600');
  const warningTextColor = useColorModeValue('gray.800', 'gray.200');
  const blueText = useColorModeValue('blue.600', 'blue.200');
  const grayText = useColorModeValue('gray.700', 'gray.200');
  const sectionBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const boxBorderColor = useColorModeValue('gray.100', 'gray.600');
  const orangeLightBg = useColorModeValue('orange.50', 'whiteAlpha.100');
  const iconOrange = useColorModeValue('#DD6B20', '#FBD38D');
  const hoverBorderColor = useColorModeValue('blue.200', 'blue.500');
  const diseaseTitleColor = useColorModeValue('blue.700', 'blue.200');
  const stateColor = useColorModeValue('green.600', 'green.200');
  const tempColor = useColorModeValue('purple.600', 'purple.200');
  const acuBorderColor = useColorModeValue('blue.100', 'whiteAlpha.200');
  const acuTextColor = useColorModeValue('blue.800', 'blue.100');
  const patientNameColor = useColorModeValue('blue.900', 'blue.100');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const treatmentProtocolBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const bottleBg = useColorModeValue('white', 'gray.700');
  const suggestionTextDark = useColorModeValue('gray.900', 'white');
  const suggestionTextNormal = useColorModeValue('gray.800', 'gray.200');
  const suggestionGreenBg = useColorModeValue('green.50', 'whiteAlpha.100');
  const suggestionGreenTitle = useColorModeValue('green.700', 'green.200');
  const saveBtnBg = useColorModeValue('#3965ff', 'blue.500');
  const saveBtnHoverBg = useColorModeValue('blue.700', 'blue.400');

  const submitShadowNormal = useColorModeValue(
    '0 4px 14px 0 rgba(49, 130, 206, 0.39)',
    '0 4px 14px 0 rgba(49, 130, 206, 0.2)',
  );
  const submitShadowHover = useColorModeValue(
    '0 6px 20px rgba(49, 130, 206, 0.23)',
    '0 6px 20px rgba(49, 130, 206, 0.1)',
  );

  /* ===================== HOOK ===================== */
  const activeSystemsForHook = React.useMemo(() => {
    if (selectedSystems.length === 0) return [];
    return [selectedSystems[selectedSystems.length - 1]];
  }, [selectedSystems]);

  const {
    systems,
    diseases: fetchedDiseases,
    generateProtocol,
    isGenerating,
    saveProtocol,

    isSaving,
  } = useProtocolPage(activeSystemsForHook);

  const systemOptions = systems?.map((item) => ({
    label: `${item.system} - ${item.systemHi}`,
    value: item.system,
  }));
  const systemLabelMap = React.useMemo(() => {
    const map = {};
    systemOptions?.forEach((opt) => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [systemOptions]);

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    if (fetchedDiseases) setDiseases(fetchedDiseases);
  }, [fetchedDiseases]);

  useEffect(() => {
    if (selectedSystems.length === 0) {
      setSelectedDiseases([]);
      setSelectedDiseaseObjects([]);
      setDiseases([]);
    }
  }, [selectedSystems]);

  /* ===================== SYSTEM HANDLERS ===================== */
  const handleSelectSystem = (systemCode) => {
    setSelectedSystems((prev) => {
      if (prev.includes(systemCode)) {
        return prev.filter((s) => s !== systemCode);
      }
      return [...prev, systemCode];
    });

    setErrors((prev) => ({ ...prev, systems: '' }));
  };

  const handleRemoveSystem = (systemCode) => {
    setSelectedSystems((prev) => prev.filter((s) => s !== systemCode));

    const nextObjects = selectedDiseaseObjects.filter(
      (d) => d.system !== systemCode,
    );
    setSelectedDiseaseObjects(nextObjects);
    setSelectedDiseases(nextObjects.map((d) => d._id));

    setActiveDisease('');
  };

  /* ===================== DISEASE HANDLERS ===================== */
  const handleSelectDisease = (id) => {
    setSelectedDiseases((prev) => {
      if (prev.includes(id)) {
        return prev.filter((dId) => dId !== id);
      }
      return [...prev, id];
    });

    setSelectedDiseaseObjects((prev) => {
      const disease = diseases.find((d) => d._id === id);
      if (!disease) return prev;

      const exists = prev.some((d) => d._id === id);

      if (exists) {
        return prev.filter((d) => d._id !== id);
      }

      return [...prev, disease];
    });

    setErrors((prev) => ({ ...prev, diseases: '' }));
  };

  const handleRemoveDisease = (disease) => {
    setSelectedDiseases((p) => p.filter((id) => id !== disease._id));
    setSelectedDiseaseObjects((p) => p.filter((d) => d._id !== disease._id));
  };

  const convertBottleStringToObject = (value) => {
    if (value && typeof value === 'object') {
      return {
        R1: value.R1 || '',
        R2: value.R2 || '',
        E: value.E || '',
      };
    }

    const parts = (value || '').split('+').map((v) => v.trim());

    return {
      R1: parts[0] || '',
      R2: parts[1] || '',
      E: parts[2] || '',
    };
  };

  /* ===================== SUBMIT ===================== */
  const handleSubmit = async () => {
    let hasError = false;

    if (selectedSystems.length === 0) {
      setErrors((prev) => ({
        ...prev,
        systems: 'Please select a system',
      }));
      hasError = true;
    }

    if (selectedDiseases.length === 0) {
      setErrors((prev) => ({
        ...prev,
        diseases: 'Please select a disease',
      }));
      hasError = true;
    }

    if (hasError) return;

    const payload = {
      systems: selectedSystems,
      diseases: selectedDiseases,
    };

    const res = await generateProtocol(payload);
    const actualData = res?.data;

    if (actualData) {
      const bottles = {
        bottle1: actualData.bottle1,
        bottle2: actualData.bottle2,
        bottle3: actualData.bottle3,
        bottle4: actualData.bottle4,
      };

      const bottlesForPatient = {
        bottle1: actualData.bottle1,
        bottle2: actualData.bottle2,
        bottle3: actualData.bottle3,
        bottle4: actualData.bottle4,
      };

      setProtocolData(bottles);
      setEditData(bottles);
      setEditDataForPatient(bottlesForPatient);
      setFinalDiseases(selectedDiseaseObjects);
      setFinalSystems(selectedSystems);
      setFullData(actualData);
      toast({
        title: 'Protocol fetched successfully',
        status: 'success',
      });
    }
  };

  const {
    mutate: saveTreatmentProtocol,
    isLoading: isPatientTreatmentProtocolSaving,
  } = useSavePatientTreatmentProtocol({
    onSuccess: () => {
      setBp('');
      setSystolic('');
      setDiastolic('');
      setSugar('');
      setNotes('');
      setSelectedPatient(null);
    },
    onError: (data) => {
      if (data?.isRemarkSubmitted === false) {
        setProtocolObjectId(data?.protocolId);
        setIsRemarkOpen(true);
      }
    },
  });

  const { data, isLoading: isIdLoading } =
    useProtocolDetailsByObjId(protocolObjectId);

  const isReliefValid = React.useMemo(() => {
    if (!data?.diseases?.length) return false;

    return data.diseases.every((d) => {
      const val = remarkData.reliefPercent[d._id];
      return (
        val !== undefined &&
        val !== '' &&
        Number(val) >= 10 &&
        Number(val) <= 100
      );
    });
  }, [remarkData.reliefPercent, data?.diseases]);

  const isFormValid = isReliefValid;

  console.log(data, '----------------------Data____________');

  const EMPTY_BOTTLE = { R1: '', R2: '', E: '' };

  const getBottleData = (key) => {
    const editedBottle = editDataForPatient?.[key];

    const isEdited =
      editedBottle &&
      typeof editedBottle === 'object' &&
      Object.values(editedBottle).some(
        (v) => typeof v === 'string' && v.trim() !== '',
      );

    if (isEdited) {
      return editedBottle;
    }

    return protocolData?.[key] || EMPTY_BOTTLE;
  };

  const validateBottles = () => {
    const errors = {};

    if (!getBottleData('bottle1')?.R1) {
      errors.bottle1 = { R1: 'Required' };
    }

    setFieldErrorsForPatientRemedy(errors);

    return Object.keys(errors).length === 0;
  };

  const getBpValidation = () => {
    const s = Number(systolic);
    const d = Number(diastolic);
    const errors = {};

    if (systolic?.trim()) {
      if (isNaN(s) || s < 70 || s > 250) {
        errors.systolic = 'Systolic must be between 70-250 mmHg';
      }
    }

    if (diastolic?.trim()) {
      if (isNaN(d) || d < 40 || d > 150) {
        errors.diastolic = 'Diastolic must be between 40-150 mmHg';
      }
    }

    if (systolic?.trim() && diastolic?.trim()) {
      if (!errors.systolic && !errors.diastolic && s <= d) {
        errors.bp = 'Systolic must be higher than Diastolic';
      }
    }

    return errors;
  };

  const getSugarValidation = () => {
    if (!sugar?.trim()) return null;
    const val = Number(sugar);
    if (isNaN(val) || val < 20 || val > 600) {
      return 'Glucose must be between 20-600 mg/dL';
    }
    return null;
  };

  const handleRemedyForPatient = () => {
    if (!selectedPatient?._id) return;

    const bpErrors = getBpValidation();
    const sugarError = getSugarValidation();

    if (
      bpErrors.systolic ||
      bpErrors.diastolic ||
      bpErrors.bp ||
      sugarError ||
      !systolic?.trim() ||
      !diastolic?.trim()
    ) {
      toast({
        title: 'Validation Error',
        description: 'Please enter valid BP and Glucose levels.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const isValid = validateBottles();
    if (!isValid) return;

    const payload = {
      system: finalSystems,
      diseases: finalDiseases.map((d) => d._id),
      patientObjId: selectedPatient._id,

      bottle1: getBottleData('bottle1'),
      bottle2: getBottleData('bottle2'),
      bottle3: getBottleData('bottle3'),
      bottle4: getBottleData('bottle4'),

      bp: `${systolic}/${diastolic}`,
      sugar: sugar || '',
      notes: notes || '',
    };

    saveTreatmentProtocol(payload);
  };

  /* ===================== UI ===================== */

  const handleEditAll = () => {
    setIsEditAll(true);
  };

  const handleCancelAll = () => {
    setEditData(protocolData);
    setIsEditAll(false);
  };

  const buildSavePayload = () => ({
    diseases: selectedDiseases,
    bottle1: convertBottleStringToObject(editData?.bottle1),
    bottle2: convertBottleStringToObject(editData?.bottle2),
    bottle3: convertBottleStringToObject(editData?.bottle3),
    bottle4: convertBottleStringToObject(editData?.bottle4),

    bottle12Remedies: protocolData?.bottle12Remedies || [],
    bottle34Remedies: protocolData?.bottle34Remedies || [],
  });

  const handleSaveAll = async () => {
    const newErrors = {};

    [1].forEach((bottleNo) => {
      const bottleKey = `bottle${bottleNo}`;
      const bottleObj = editData?.[bottleKey];

      if (!bottleObj?.R1) {
        newErrors[bottleKey] = {
          R1: 'R1 is required',
        };
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    try {
      const payload = buildSavePayload();
      await saveProtocol(payload);
      setProtocolData(editData);
      setIsEditAll(false);
    } catch (e) {
      console.error(e);
    }
  };

  const fieldMap1 = ['R1', 'R2', 'E'];
  const handleBottleChange = (key, index, value) => {
    const fieldKey = fieldMap1[index];

    setEditData((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { R1: '', R2: '', E: '' }),
        [fieldKey]: value,
      },
    }));
  };

  const fieldMap = ['R1', 'R2', 'E'];

  const handleBottleChangeForPatient = (key, index, value) => {
    const fieldKey = fieldMap[index];

    setEditDataForPatient((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { R1: '', R2: '', E: '' }),
        [fieldKey]: value,
      },
    }));

    if (fieldKey === 'R1' && value) {
      setFieldErrorsForPatientRemedy((prev) => ({
        ...prev,
        [key]: undefined,
      }));
    }
  };

  return (
    <Box mt="5" bg={pageBg} p={4}>
      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        spacing={5}
        h="calc(100vh - 100px)"
        templateColumns={{ base: '1fr', md: '30% 68%' }}
      >
        {/* LEFT */}
        <Box
          bg={cardBg}
          p={6}
          rounded="xl"
          shadow="lg"
          border="1px"
          borderColor={boxBorderColor}
          maxH="90vh"
          overflowY="auto"
        >
          <VStack align="stretch" spacing={5}>
            {/* SYSTEM SELECT SECTION */}
            <Box>
              <FormControl mb={1} isInvalid={!!errors.systems}>
                <FormLabel
                  color={blueText}
                  fontWeight="bold"
                  fontSize="xs"
                  textTransform="uppercase"
                >
                  Select System/Axis
                </FormLabel>

                <MultiSelectDropdown
                  systemSelect={true}
                  label="System"
                  placeholder="System / Axis"
                  options={systemOptions}
                  selectedValues={selectedSystems}
                  onToggle={handleSelectSystem}
                />

                <FormErrorMessage>{errors.systems}</FormErrorMessage>
              </FormControl>

              <Box mt={2}>
                <SelectedChips
                  items={selectedSystems}
                  getKey={(sys) => sys}
                  getLabel={(sys) => systemLabelMap[sys] || sys}
                  onRemove={handleRemoveSystem}
                  chipBg={chipBg}
                  chipText={chipText}
                />
              </Box>
            </Box>

            <Divider />

            {/* DISEASE SELECT SECTION */}
            <Box>
              <FormControl mb={1} isInvalid={!!errors.diseases}>
                <FormLabel
                  color={blueText}
                  fontWeight="bold"
                  fontSize="xs"
                  textTransform="uppercase"
                >
                  Select Disease/Symptoms
                </FormLabel>

                <MultiSelectDropdown
                  label="Disease"
                  placeholder="Choose Disease / Symptoms"
                  options={diseases.map((d) => ({
                    label: d.diseaseHindi
                      ? `${d.diseaseEnglish} - ${d.diseaseHindi}`
                      : d.diseaseEnglish,
                    value: d._id,
                  }))}
                  selectedValues={selectedDiseases}
                  onToggle={handleSelectDisease}
                  isDisabled={selectedSystems.length === 0}
                />

                <FormErrorMessage>{errors.diseases}</FormErrorMessage>
              </FormControl>

              {/* Modern Tooltip/Warning Style */}
              <Flex align="center" mt={2} mb={2}>
                <Box boxSize="6px" borderRadius="full" bg="orange.400" mr={2} />
                <Text
                  fontWeight="medium"
                  fontSize="xs"
                  color={warningTextColor}
                >
                  First selected disease will have highest priority.
                </Text>
              </Flex>

              <SelectedChips
                items={selectedDiseaseObjects}
                getKey={(d) => d._id}
                getLabel={(d) =>
                  d.diseaseHindi
                    ? `${d.diseaseEnglish} - ${d.diseaseHindi}`
                    : d.diseaseEnglish
                }
                onRemove={handleRemoveDisease}
                chipBg={chipBg}
                chipText={chipText}
              />
            </Box>

            {/* SUBMIT BUTTON - Modern Styling */}
            <Button
              colorScheme="blue"
              width="full"
              size="lg"
              py={7}
              fontSize="md"
              fontWeight="bold"
              borderRadius="lg"
              onClick={handleSubmit}
              isLoading={isGenerating}
              boxShadow={submitShadowNormal}
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: submitShadowHover,
              }}
              _active={{
                transform: 'translateY(0)',
              }}
            >
              Submit
            </Button>
          </VStack>
        </Box>

        {/* RIGHT */}
        <Box
          bg={cardBg}
          p={4}
          rounded="xl"
          shadow="lg"
          border="1px solid"
          borderColor={borderClr}
          overflowY="auto"
        >
          <Box mt={4} w="full">
            {(isProtocolReady || isGenerating) && (
              <Box
                mb={5}
                p={4}
                bg={orangeLightBg}
                borderLeft="4px solid"
                borderColor="orange.400"
                rounded="md"
              >
                <Text
                  fontWeight={'bold'}
                  fontSize="xs"
                  color={warningTextColor}
                  mb={3}
                  display="flex"
                  alignItems="center"
                >
                  <Icon as={InfoIcon} mr={2} />
                  *Please select a registered patient in order to assign the
                  specified protocol as treatment.
                </Text>
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<ViewIcon />}
                  onClick={() => setIsOpen(true)}
                  boxShadow="sm"
                >
                  Patient List
                </Button>
              </Box>
            )}

            {/* ================= Selected Diseases ================= */}
            <Box
              mb={6}
              bg={rightBg}
              p={5}
              rounded="xl"
              shadow="sm"
              border="1px solid"
              borderColor={boxBorderColor}
              borderLeft="6px solid"
            >
              <Flex align="center" mb={4}>
                <Box p={1.5} bg={orangeLightBg} rounded="md" mr={3}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={iconOrange}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                  </svg>
                </Box>
                <Text
                  fontWeight="bold"
                  fontSize="sm"
                  color={grayText}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Selected Diseases & Clinical Details
                </Text>
              </Flex>

              {finalDiseases.length === 0 ? (
                <Center
                  p={8}
                  border="2px dashed"
                  borderColor={borderClr}
                  rounded="lg"
                >
                  <Text color={textMuted} fontSize="sm">
                    No disease selected.
                  </Text>
                </Center>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
                  {finalDiseases.map((d, idx) => (
                    <Box
                      key={idx}
                      p={4}
                      borderRadius="xl"
                      border="1px solid"
                      borderColor={borderClr}
                      bg={cardBg}
                      position="relative"
                      transition="all 0.2s"
                      _hover={{ shadow: 'md', borderColor: hoverBorderColor }}
                    >
                      {/* Priority Indicator for the first item */}
                      {idx === 0 && (
                        <Badge
                          position="absolute"
                          top="-2"
                          right="3"
                          colorScheme="orange"
                          variant="solid"
                          fontSize="xs"
                          borderRadius="full"
                        >
                          Primary
                        </Badge>
                      )}

                      {/* Disease Header */}
                      <VStack align="start" spacing={0} mb={4}>
                        <Text
                          fontSize="md"
                          fontWeight="bold"
                          color={diseaseTitleColor}
                          noOfLines={1}
                        >
                          {d.diseaseEnglish}
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          color={textMuted}
                          fontStyle="italic"
                        >
                          {d.diseaseHindi || 'N/A'}
                        </Text>
                      </VStack>

                      <Divider mb={3} opacity={0.6} />

                      {/* Clinical Parameters Grid */}
                      <SimpleGrid columns={2} spacing={2} mb={4}>
                        <Box>
                          <Text
                            fontSize="10px"
                            fontWeight="bold"
                            color={textMuted}
                            textTransform="uppercase"
                          >
                            State
                          </Text>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color={stateColor}
                          >
                            {d.state || '-'}
                          </Text>
                        </Box>

                        <Box>
                          <Text
                            fontSize="10px"
                            fontWeight="bold"
                            color={textMuted}
                            textTransform="uppercase"
                          >
                            Constitution
                          </Text>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color={tempColor}
                          >
                            {d.temperament || '-'}
                          </Text>
                        </Box>
                      </SimpleGrid>

                      {/* Treatment Target */}
                      <Box
                        bg={sectionBg}
                        p={2}
                        rounded="lg"
                        border="1px solid"
                        borderColor={acuBorderColor}
                      >
                        <Text
                          fontSize="10px"
                          fontWeight="bold"
                          color={blueText}
                          mb={1}
                          textTransform="uppercase"
                        >
                          Acupuncture Points
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color={acuTextColor}
                        >
                          {d.acupuncture_points || 'None Specified'}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </Box>
            {selectedPatient && (
              <Box
                mb="6"
                mt="4"
                p="5"
                borderWidth="1px"
                borderRadius="xl"
                borderColor={borderClr}
                bg={cardBg}
                boxShadow="xl"
              >
                {/* PATIENT HEADER CARD */}
                <Flex
                  justify="space-between"
                  align="center"
                  mb="4"
                  bg={sectionBg}
                  p={3}
                  rounded="lg"
                >
                  <HStack spacing={3}>
                    <Avatar
                      textTransform={'capitalize'}
                      size="sm"
                      name={selectedPatient.name}
                      bg="blue.500"
                      color="white"
                    />
                    <Box>
                      <Text
                        textTransform={'capitalize'}
                        fontSize="md"
                        fontWeight="bold"
                        color={patientNameColor}
                        lineHeight="1"
                      >
                        {selectedPatient.name}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={blueText}
                        fontWeight="600"
                        mt={1}
                      >
                        ID: {selectedPatient.patientId}
                      </Text>
                    </Box>
                  </HStack>
                  <Badge
                    colorScheme="blue"
                    fontSize="xs"
                    px="3"
                    py="1"
                    variant="solid"
                    borderRadius="full"
                  >
                    Age: {selectedPatient.age || 'N/A'}
                  </Badge>
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mb={4}>
                  {/* BP INPUT */}
                  <FormControl
                    isRequired
                    isInvalid={
                      !systolic?.trim() ||
                      !diastolic?.trim() ||
                      !!getBpValidation().systolic ||
                      !!getBpValidation().diastolic ||
                      !!getBpValidation().bp
                    }
                  >
                    <FormLabel
                      fontSize="xs"
                      fontWeight="bold"
                      color={labelColor}
                      mb={1}
                      display="flex"
                      alignItems="center"
                    >
                      <Icon as={RepeatIcon} mr={1} color="red.500" />
                      Blood Pressure (mmHg)
                    </FormLabel>

                    <HStack spacing={2} align="start">
                      {/* Systolic */}
                      <VStack align="stretch" flex={1}>
                        <Input
                          size="sm"
                          placeholder="Systolic Ex.120"
                          value={systolic}
                          type="number"
                          onChange={(e) => setSystolic(e.target.value)}
                          bg={inputBg}
                          focusBorderColor="blue.400"
                          borderRadius="md"
                        />
                        {getBpValidation().systolic && (
                          <Text fontSize="10px" color="red.500">
                            {getBpValidation().systolic}
                          </Text>
                        )}
                      </VStack>

                      <Text fontWeight="bold" mt={1}>
                        /
                      </Text>

                      {/* Diastolic */}
                      <VStack align="stretch" flex={1}>
                        <InputGroup size="sm">
                          <Input
                            placeholder="Diastolic Ex.80"
                            value={diastolic}
                            type="number"
                            onChange={(e) => setDiastolic(e.target.value)}
                            bg={inputBg}
                            focusBorderColor="blue.400"
                            borderRadius="md"
                          />
                        </InputGroup>
                        {getBpValidation().diastolic && (
                          <Text fontSize="10px" color="red.500">
                            {getBpValidation().diastolic}
                          </Text>
                        )}
                      </VStack>
                    </HStack>

                    {!systolic?.trim() && !diastolic?.trim() ? (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        Blood Pressure is required
                      </Text>
                    ) : getBpValidation().bp ? (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        {getBpValidation().bp}
                      </Text>
                    ) : null}
                  </FormControl>

                  {/* GLUCOSE INPUT */}
                  <FormControl isInvalid={!!getSugarValidation()}>
                    <FormLabel
                      fontSize="xs"
                      fontWeight="bold"
                      color={labelColor}
                      mb={1}
                      display="flex"
                      alignItems="center"
                    >
                      <Icon as={StarIcon} mr={1} color="green.500" /> Glucose
                      Level (mg/dL)
                    </FormLabel>
                    <InputGroup size="sm">
                      <Input
                        placeholder="110 mg/dL"
                        value={sugar}
                        type="number"
                        onChange={(e) => setSugar(e.target.value)}
                        bg={inputBg}
                        focusBorderColor="blue.400"
                        borderRadius="md"
                      />
                    </InputGroup>
                    {getSugarValidation() && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        {getSugarValidation()}
                      </Text>
                    )}
                  </FormControl>
                </SimpleGrid>

                {/* CLINICAL NOTES */}
                <FormControl mb={5}>
                  <FormLabel
                    fontSize="xs"
                    fontWeight="bold"
                    color={labelColor}
                    mb={1}
                  >
                    Clinical Notes (optional)
                  </FormLabel>
                  <Textarea
                    size="sm"
                    placeholder="Enter clinical observations, diagnosis, and treatment plan..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    bg={inputBg}
                    focusBorderColor="blue.400"
                    fontSize="xs"
                    borderRadius="md"
                    rows={3}
                    p="3"
                  />
                </FormControl>

                {/* TREATMENT PROTOCOL SECTION */}
                <Box
                  p="4"
                  borderWidth="1px"
                  borderRadius="lg"
                  bg={treatmentProtocolBg}
                  borderColor={borderClr}
                >
                  <Flex align="center" mb="4">
                    <Icon as={EditIcon} mr={2} color="blue.500" />
                    <Text fontSize="sm" fontWeight="bold" color={grayText}>
                      Treatment Protocol
                    </Text>
                  </Flex>

                  <VStack spacing={4} align="stretch">
                    {[1, 2, 3, 4].map((num) => (
                      <Flex
                        key={num}
                        align="center"
                        gap={4}
                        p={2}
                        bg={bottleBg}
                        rounded="md"
                        shadow="sm"
                      >
                        <Badge
                          variant="outline"
                          colorScheme="gray"
                          minW="75px"
                          textAlign="center"
                          py={1}
                        >
                          BOTTLE {num}
                        </Badge>
                        <Box flex={1}>
                          <BottleItem
                            bottleIndex={num}
                            error={
                              fieldErrorsForPatientRemedy?.[`bottle${num}`]?.R1
                            }
                            value={
                              isEditAllForPatient
                                ? editDataForPatient?.[`bottle${num}`]
                                : protocolData?.[`bottle${num}`]
                            }
                            isEdit={true}
                            suggestions={
                              num <= 2
                                ? protocolData?.bottle12Remedies
                                : protocolData?.bottle34Remedies || []
                            }
                            onChange={(i, v) =>
                              handleBottleChangeForPatient(`bottle${num}`, i, v)
                            }
                          />
                        </Box>
                      </Flex>
                    ))}
                  </VStack>
                </Box>

                {/* SAVE ACTION */}
                <Flex justify="flex-end" mt="6">
                  <Button
                    size="md"
                    colorScheme="blue"
                    leftIcon={<CheckCircleIcon />}
                    px="8"
                    h="45px"
                    fontSize="sm"
                    fontWeight="bold"
                    borderRadius="full"
                    bg={saveBtnBg}
                    _hover={{
                      bg: saveBtnHoverBg,
                      transform: 'scale(1.02)',
                    }}
                    _active={{ transform: 'scale(0.98)' }}
                    transition="all 0.2s"
                    onClick={handleRemedyForPatient}
                    isLoading={isPatientTreatmentProtocolSaving}
                    loadingText="Saving..."
                    shadow="lg"
                  >
                    Save Treatment
                  </Button>
                </Flex>
              </Box>
            )}
          </Box>

          <Divider mb={6} />

          {/* ================= Protocol Card ================= */}
          <Text
            bg={useColorModeValue('white', 'gray.800')}
            px={4}
            fontSize="xs"
            fontWeight="bold"
            color="blue.500"
            textTransform="uppercase"
            letterSpacing="widest"
            mt={10}
          >
            Standard Guided Protocol
          </Text>
          <Text
            fontWeight={'bold'}
            textAlign={'end'}
            fontSize="xs"
            color={warningTextColor}
          >
            *To Save Standard Protocol, Click on Edit, make changes and Save All
          </Text>
          <Box
            bg={cardBg}
            p={5}
            rounded="2xl"
            borderLeft="5px solid"
            borderColor="blue.500"
            boxShadow="xl"
            position="relative"
            overflow="hidden"
          >
            {/* Decorative Accent */}
            <Box
              position="absolute"
              top={0}
              right={0}
              w="100px"
              h="100px"
              bg="blue.500"
              // opacity={0}
              borderRadius="0 0 0 100px"
              zIndex={0}
            />

            {/* ===== Edit Buttons ===== */}
            <Flex
              justify="flex-end"
              mb={4}
              position="relative"
              zIndex={1}
              gap={2}
            >
              {!isEditAll ? (
                <Button
                  size="sm"
                  onClick={handleEditAll}
                  isDisabled={!isProtocolReady || isGenerating}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={handleSaveAll}
                    isDisabled={isSaving}
                  >
                    Save All
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={handleCancelAll}
                    isDisabled={isGenerating}
                  >
                    Close
                  </Button>
                </>
              )}
            </Flex>

            {/* ================= Bottles ================= */}

            <VStack spacing={4} align="stretch">
              {[1, 2, 3, 4].map((num) => (
                <Flex
                  key={num}
                  align="center"
                  gap={4}
                  p={2}
                  bg={bottleBg}
                  rounded="md"
                  shadow="sm"
                >
                  <Badge
                    variant="outline"
                    colorScheme="gray"
                    minW="75px"
                    textAlign="center"
                    py={1}
                  >
                    BOTTLE {num}
                  </Badge>
                  <Box flex={1}>
                    <BottleItem
                      error={fieldErrors?.[`bottle${num}`]?.R1}
                      isEdit={isEditAll}
                      suggestions={
                        num <= 2
                          ? protocolData?.bottle12Remedies
                          : protocolData?.bottle34Remedies || []
                      }
                      onChange={(i, v) =>
                        handleBottleChange(`bottle${num}`, i, v)
                      }
                      bottleIndex={num}
                      value={
                        isEditAll
                          ? editData?.[`bottle${num}`]
                          : protocolData?.[`bottle${num}`]
                      }
                    />
                  </Box>
                </Flex>
              ))}
            </VStack>
          </Box>
          {fullData?.remainingBottle1And2Remedy && (
            <Box
              mt={6}
              p={4}
              borderLeft="4px solid"
              borderColor="blue.400"
              bg={sectionBg}
              borderRadius="md"
            >
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={diseaseTitleColor}
                mb={1}
              >
                Bottle 1 & 2 – Suggestion
              </Text>
              <Text fontSize="sm" color={grayText}>
                You can <b>replace or adjust the remedies</b> in
                <b> Bottle 1 and Bottle 2</b> before saving this protocol.
              </Text>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={suggestionTextDark}
                mt={2}
              >
                Suggested remaining remedies:
              </Text>
              <Text fontSize="sm" color={suggestionTextNormal}>
                {fullData.remainingBottle1And2Remedy}
              </Text>
            </Box>
          )}

          {fullData?.remainingBottle3And4Remedy && (
            <Box
              mt={6}
              p={4}
              borderLeft="4px solid"
              borderColor="green.400"
              bg={suggestionGreenBg}
              borderRadius="md"
            >
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={suggestionGreenTitle}
                mb={1}
              >
                Bottle 3 & 4 – Suggestion
              </Text>
              <Text fontSize="sm" color={grayText}>
                You may <b>replace or fine-tune the remedies</b> in
                <b> Bottle 3 and Bottle 4</b> as needed before saving.
              </Text>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={suggestionTextDark}
                mt={2}
              >
                Suggested remaining remedies:
              </Text>
              <Text fontSize="sm" color={suggestionTextNormal}>
                {fullData.remainingBottle3And4Remedy}
              </Text>
            </Box>
          )}
        </Box>
      </SimpleGrid>
      <PatientListModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        patients={patientHistory}
        isLoading={isLoading}
        searchValue={searchString}
        onSearchChange={setSearchString}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        onSelectPatient={(patient) => setSelectedPatient(patient)}
      />

      <Modal
        isOpen={isRemarkOpen}
        onClose={() => setIsRemarkOpen(false)}
        size={{ base: 'full', md: 'md' }}
        isCentered
      >
        <ModalOverlay bg="blackAlpha.700" />

        <ModalContent borderRadius="xl">
          <ModalHeader bg="blue.400" color="white">
            Add Patient Relief
          </ModalHeader>

          <ModalCloseButton color="white" />

          <ModalBody p={6}>
            <Flex direction="column" gap={4}>
              {/* SCORE */}
              {/* <Box>
                <Flex justify="space-between" mb={1}>
                  <Text fontSize="xs" color={textMuted}>
                    Score Remedy
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color="orange.500">
                    {remarkData.score || 0}/5
                  </Text>
                </Flex>

                <Slider
                  value={Number(remarkData.score) || 0}
                  min={0}
                  max={5}
                  step={1}
                  onChange={(val) => handleRemarkChange('score', val)}
                  colorScheme={
                    remarkData.score <= 2
                      ? 'red'
                      : remarkData.score <= 4
                        ? 'yellow'
                        : 'green'
                  }
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>

                  <SliderThumb boxSize={4}>
                    <Box color="orange.500" />
                  </SliderThumb>
                </Slider>

                <Flex justify="space-between" mt={1}>
                  <Text fontSize="10px" color={textMuted}>
                    Poor
                  </Text>
                  <Text fontSize="10px" color={textMuted}>
                    Excellent
                  </Text>
                </Flex>
                {!isScoreValid && (
                  <Text fontSize="10px" color="red.500" mt={1}>
                    Score is required
                  </Text>
                )}
              </Box> */}

              {/* RELIEF */}
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color={blueText}
                  mb={3}
                  textTransform="uppercase"
                >
                  Clinical Improvement by Disease
                </Text>
                <VStack align="stretch" spacing={4}>
                  {data?.diseases?.map((disease) => {
                    const reliefValue =
                      remarkData?.reliefPercent?.[disease._id] || '';

                    return (
                      <Box
                        key={disease._id}
                        p={3}
                        bg={treatmentProtocolBg}
                        rounded="lg"
                        border="1px solid"
                        borderColor={boxBorderColor}
                      >
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text fontWeight="bold" fontSize="sm">
                            {disease.diseaseEnglish}
                          </Text>
                          <Text fontSize="xs" color={textMuted}>
                            Relief (%)
                          </Text>
                        </Flex>

                        <Input
                          type="number"
                          size="sm"
                          placeholder="10 - 100"
                          value={reliefValue}
                          bg={inputBg}
                          onChange={(e) =>
                            handleRemarkChange(disease._id, e.target.value)
                          }
                        />

                        {reliefValue &&
                          (Number(reliefValue) < 10 ||
                            Number(reliefValue) > 100) && (
                            <Text fontSize="10px" color="red.500" mt={1}>
                              Enter relief between 10 and 100
                            </Text>
                          )}
                      </Box>
                    );
                  })}
                </VStack>
              </Box>

              {/* REMARK */}
              {/* <Box>
                <Text fontSize="xs" mb={1} color={textMuted}>
                  Remark
                </Text>
                <Textarea
                  size="sm"
                  resize="none"
                  rows={3}
                  placeholder="Treatment works well…"
                  value={remarkData.remark}
                  onChange={(e) => handleRemarkChange('remark', e.target.value)}
                />
                {!isRemarkValid && (
                  <Text fontSize="10px" color="red.500" mt={1}>
                    Remark is required
                  </Text>
                )}
              </Box> */}

              {/* SUBMIT */}
              <Flex justify="flex-end" gap={3} mt={2}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsRemarkOpen(false)}
                >
                  Cancel
                </Button>

                <Button
                  size="sm"
                  colorScheme="green"
                  isDisabled={!isFormValid}
                  isLoading={isAddingRemark}
                  onClick={() => {
                    const diseaseReliefs = data?.diseases?.map((d) => ({
                      diseaseId: d._id,
                      reliefPercent: Number(remarkData.reliefPercent[d._id]),
                    }));

                    const payload = {
                      patientObjId: selectedPatient._id,
                      givenTreatmentProtocolId: protocolObjectId,
                      score: Number(remarkData.score),
                      diseaseReliefs,
                      remark: remarkData.remark.trim(),
                    };

                    addRemarkMutation(payload);
                  }}
                >
                  Submit
                </Button>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
